import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Play, Video, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const translations = {
  en: { title: "Watch Our Videos", subtitle: "Testimonials and product presentations", viewAll: "View All Videos" },
  ro: { title: "Urmărește Video-urile Noastre", subtitle: "Testimoniale și prezentări de produse", viewAll: "Vezi Toate Video-urile" },
  es: { title: "Mira Nuestros Videos", subtitle: "Testimonios y presentaciones de productos", viewAll: "Ver Todos los Videos" },
  it: { title: "Guarda i Nostri Video", subtitle: "Testimonianze e presentazioni di prodotti", viewAll: "Vedi Tutti i Video" },
  de: { title: "Unsere Videos Ansehen", subtitle: "Testimonials und Produktpräsentationen", viewAll: "Alle Videos Ansehen" }
};

const VideoSection = ({ className = '' }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations['en'];
  const [videos, setVideos] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/videos?display_on=homepage`);
        setVideos((response.data || []).slice(0, 3));
      } catch (error) {
        console.error('Error fetching videos:', error);
      }
    };

    fetchVideos();
  }, []);

  const getEmbedUrl = (video) => {
    if (video.video_type === 'youtube') {
      const match = video.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
      if (match) return `https://www.youtube.com/embed/${match[1]}`;
    }
    return video.video_url;
  };

  const getThumbnail = (video) => {
    if (video.thumbnail_url) return video.thumbnail_url;
    if (video.video_type === 'youtube') {
      const match = video.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
      if (match) return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
    }
    return null;
  };

  if (videos.length === 0) return null;

  return (
    <section className={`py-16 bg-slate-900 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sky-400 font-semibold uppercase tracking-wide text-sm mb-2">Video</p>
          <h2 className="text-3xl font-bold text-white mb-2">{t.title}</h2>
          <p className="text-slate-400">{t.subtitle}</p>
        </div>

        {/* Videos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {videos.map((video) => (
            <div 
              key={video.id}
              className="rounded-2xl overflow-hidden bg-slate-800 group"
            >
              <div className="aspect-video relative">
                {/* YouTube embed when playing */}
                {playingVideo === video.id && video.video_type === 'youtube' ? (
                  <iframe
                    src={`${getEmbedUrl(video)}?autoplay=1`}
                    title={video.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : /* Native video player for uploaded videos */
                playingVideo === video.id && (video.video_type === 'upload' || video.video_type === 'link') ? (
                  <video
                    src={video.video_url}
                    controls
                    autoPlay
                    className="w-full h-full object-contain bg-black"
                    onEnded={() => setPlayingVideo(null)}
                  />
                ) : (
                  <>
                    {/* Thumbnail or video preview */}
                    {getThumbnail(video) ? (
                      <img 
                        src={getThumbnail(video)} 
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : video.video_type === 'upload' || video.video_type === 'link' ? (
                      <video 
                        src={video.video_url} 
                        className="w-full h-full object-cover"
                        preload="metadata"
                        muted
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-700">
                        <Video className="w-12 h-12 text-slate-500" />
                      </div>
                    )}
                    
                    <button
                      onClick={() => {
                        if (video.video_type === 'youtube' || video.video_type === 'upload' || video.video_type === 'link') {
                          setPlayingVideo(video.id);
                        } else {
                          window.open(video.video_url, '_blank');
                        }
                      }}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/50 transition-colors"
                    >
                      <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                        <Play className="w-7 h-7 text-sky-600 fill-sky-600 ml-1" />
                      </div>
                    </button>
                  </>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-bold text-white truncate">{video.title}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center">
          <Link 
            to="/videos"
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 font-medium transition-colors"
          >
            {t.viewAll}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
